import React, {
    ReactNode,
    FC,
    RefObject,
    useState,
    useRef,
    useEffect,
} from 'react';
import {
    Tooltip as BaseTooltip,
    tooltipClasses,
    TooltipProps as BaseTooltipProps,
    TypographyProps,
} from '@mui/material';

import { useSizeContext } from 'Modules/ui';

import { Typography } from '../Typography';


export type TooltipProps = Omit<BaseTooltipProps, 'title'> & {
    header?: ReactNode;
    text?: ReactNode;
    content?: ReactNode;
    background?: string;
    px?: string | number;
    py?: string | number;
    headerProps?: TypographyProps;
    textProps?: TypographyProps;
    componentPropsTooltipSx?: TooltipProps['componentsProps']['tooltip']['sx'];
};

export const Tooltip: FC<TooltipProps> = React.forwardRef(
    (
        {
            placement,
            text,
            header,
            children,
            background = 'grayscale.shade95',
            px,
            py,
            headerProps,
            textProps,
            componentPropsTooltipSx = {},
            content,
            ...props
        },
        ref,
    ) => (
        <BaseTooltip
            data-testid='tooltip'
            ref={ref}
            placement={placement}
            arrow
            enterTouchDelay={0}
            leaveDelay={50}
            PopperProps={{
                modifiers: [
                    {
                        name: 'offset',
                        options: {
                            offset: ({ placement }) => {
                                if (placement === 'top-end')
                                    return [10, 0];

                                if (placement === 'top-start')
                                    return [-10, 0];

                                if (placement === 'right')
                                    return [-8, 0];

                                if (placement === 'left')
                                    return [8, 0];
                                if (placement === 'bottom-end')
                                    return [8, -10];
                                return [];
                            },
                        },
                    },
                    {
                        name: 'arrow',
                        options: {
                            padding: 16,
                        },
                    },
                ],
            }}
            componentsProps={{
                popper: {
                    sx: {
                        [`&.${tooltipClasses.popper}[data-popper-placement*="top"] .${tooltipClasses.arrow}`]:
                        {
                            mb: -2,
                            '&::before': {
                                transformOrigin: 'center',
                            },
                        },
                        [`&.${tooltipClasses.popper}[data-popper-placement*="right"] .${tooltipClasses.arrow}`]:
                        {
                            ml: -1,
                            '&::before': {
                                transformOrigin: 'center',
                            },
                        },
                        [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.arrow}`]:
                        {
                            mt: -1,
                            '&::before': {
                                transformOrigin: 'center',
                            },
                        },
                        [`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.arrow}`]:
                        {
                            mr: -0.5,
                            '&::before': {
                                transformOrigin: 'center',
                            },
                        },
                    },
                },
                tooltip: {
                    sx: {
                        backgroundColor: background,
                        py: py || 3,
                        px: px || 4,
                        borderRadius: 2,
                        lineHeight: 0,
                        ...componentPropsTooltipSx,
                    },
                },
                arrow: {
                    sx: ({ spacing }) => ({
                        color: background,
                        width: spacing(3),
                        height: spacing(3),
                        overflow: 'visible',
                        '&::before': {
                            width: spacing(2),
                            height: spacing(2),
                            borderRadius: '1px',
                        },
                    }),
                },
            }}
            title={(
                <>
                    {header && (
                        <Typography
                            variant='boldCaption2'
                            sx={{
                                mb: 1,
                            }}
                            color='grayscale.shade0'
                            {...headerProps}
                        >
                            {header}
                            <br />
                        </Typography>
                    )}
                    {text && (
                        <Typography
                            variant='regularCaption2'
                            color='grayscale.shade20'
                            {...textProps}
                        >
                            {text}
                        </Typography>
                    )}
                    {content}
                </>
            )}
            {...props}
        >
            {children}
        </BaseTooltip>
    ),
);

export const useTooltipPropsToOnlyEllipsisComponent =
    (): Partial<TooltipProps> & {
        componentRef: RefObject<HTMLDivElement>;
    } => {
        const [disabled, setDisabled] = useState(true);

        const { viewportHeight, viewportWidth } = useSizeContext();
        const ref = useRef<HTMLDivElement>(null);
        useEffect(() => {
            if (
                ref.current?.offsetWidth < ref.current?.scrollWidth ||
                ref.current?.offsetHeight < ref.current?.scrollHeight
            )
                setDisabled(false);
            else
                setDisabled(true);
        }, [viewportWidth, viewportHeight]);

        return {
            componentRef: ref,
            disableHoverListener: disabled,
        };
    };
